import React, { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RateReviewIcon from '@mui/icons-material/RateReview';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { apiUrl, fetchJson } from '../../apiBase';
import {
  createObservationEntry,
  deleteObservationEntry,
  getObservationSchema,
  listObservationEntries,
  sendObservationEntry,
  updateObservationEntry,
} from '../api/observationJournal';
import ObservationStructuredForm from './ObservationStructuredForm';
import { observationScaleChipSx, observationScaleTone } from './observationScaleUi';

const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const roleLabels = {
  teacher: 'Öğretmen',
  admin: 'Yönetici',
  superadmin: 'Yönetici',
};

const statusConfig = {
  sent: { label: 'Gönderildi', chipColor: 'success', chipVariant: 'filled' },
  pending: { label: 'Kuyrukta', chipColor: 'warning', chipVariant: 'outlined' },
  unknown: { label: 'Durum belirsiz', chipColor: 'warning', chipVariant: 'outlined' },
  failed: { label: 'Başarısız', chipColor: 'error', chipVariant: 'outlined' },
  skipped: { label: 'Atlandı', chipColor: 'default', chipVariant: 'outlined' },
  draft: { label: 'Taslak', chipColor: 'default', chipVariant: 'outlined' },
};

const statusTone = {
  sent: { border: '#A5D6A7', background: 'linear-gradient(180deg, #FFFFFF 0%, #F4FBF4 100%)' },
  pending: { border: '#FFCC80', background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF8EF 100%)' },
  failed: { border: '#EF9A9A', background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF4F4 100%)' },
  unknown: { border: '#FFCC80', background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF8EF 100%)' },
  skipped: { border: '#CFD8DC', background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)' },
  draft: { border: '#CFD8DC', background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)' },
};

const normalizeMessageStatus = (value) => String(value || '').trim().toLowerCase() || 'draft';
const scaleLabels = {
  iyi: 'İyi',
  orta: 'Orta',
  gelistirilmeli: 'Geliştirilmeli',
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return dateFormatter.format(date);
  } catch (_) {
    return date.toLocaleString('tr-TR');
  }
};

const emptyObservationPayload = () => ({ academic: {}, general: {}, note: '' });

const normalizeSchema = (schema) => {
  if (!schema || typeof schema !== 'object') return null;
  return {
    schema_version: schema.schema_version || 'structured_v1',
    grade_level: schema.grade_level || null,
    academic_fields: Array.isArray(schema.academic_fields) ? schema.academic_fields : [],
    general_fields: Array.isArray(schema.general_fields) ? schema.general_fields : [],
    scale: Array.isArray(schema.scale) ? schema.scale : [
      { key: 'iyi', label: 'İyi' },
      { key: 'orta', label: 'Orta' },
      { key: 'gelistirilmeli', label: 'Geliştirilmeli' },
    ],
    submission_rules: schema.submission_rules || {
      all_visible_structured_fields_required: false,
      allow_note_only: true,
    },
  };
};

const normalizeObservationPayload = (payload) => ({
  academic: payload && typeof payload.academic === 'object' ? { ...payload.academic } : {},
  general: payload && typeof payload.general === 'object' ? { ...payload.general } : {},
  note: typeof payload?.note === 'string' ? payload.note : '',
});

const schemaFromStructuredPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const snapshot = normalizeSchema(payload.schema_snapshot);
  if (snapshot) return snapshot;

  const academic = payload.academic && typeof payload.academic === 'object' ? payload.academic : {};
  const general = payload.general && typeof payload.general === 'object' ? payload.general : {};
  return normalizeSchema({
    schema_version: payload.schema_version || 'structured_v1',
    grade_level: payload.grade_level || null,
    academic_fields: Object.keys(academic).map((key) => ({ key, label: key })),
    general_fields: Object.keys(general).map((key) => ({ key, label: key })),
  });
};

const buildPayloadForSchema = (schema, payload) => {
  const normalized = normalizeObservationPayload(payload);
  if (!schema) return normalized;

  const academic = (schema.academic_fields || []).reduce((acc, field) => {
    acc[field.key] = normalized.academic[field.key] || '';
    return acc;
  }, {});
  const general = (schema.general_fields || []).reduce((acc, field) => {
    acc[field.key] = normalized.general[field.key] || '';
    return acc;
  }, {});

  return {
    academic,
    general,
    note: normalized.note,
  };
};

const isStructuredObservationEntry = (entry) => Boolean(entry?.structured_payload && typeof entry.structured_payload === 'object');

const entrySchema = (entry, fallbackSchema) => schemaFromStructuredPayload(entry?.structured_payload) || normalizeSchema(fallbackSchema);

const getEntryAuthorLabel = (entry) => {
  const explicitName = String(entry?.recorded_by_name || entry?.teacher_name || '').trim();
  if (explicitName) return explicitName;

  const fallbackRole = String(entry?.recorded_by_role || '').trim().toLowerCase();
  if (fallbackRole && roleLabels[fallbackRole]) return roleLabels[fallbackRole];

  const phone = String(entry?.recorded_by_phone || '').trim();
  if (phone) return phone;

  return 'Kayıt sahibi';
};

const hasTeacherObservation = (payload) => Boolean(String(payload?.note || '').trim());

export default function ObservationJournalPage({ schoolId, user, onError, onSuccess, openTarget }) {
  const [classOptions, setClassOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [loadedStudentClassId, setLoadedStudentClassId] = useState('');
  const [pendingOpenTarget, setPendingOpenTarget] = useState(null);
  const [entries, setEntries] = useState([]);
  const [studentSchema, setStudentSchema] = useState(null);
  const [formPayload, setFormPayload] = useState(emptyObservationPayload);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogStep, setCreateDialogStep] = useState('form');
  const [createDialogEntry, setCreateDialogEntry] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entryAction, setEntryAction] = useState({ id: '', type: '' });
  const [deleteDialogEntry, setDeleteDialogEntry] = useState(null);
  const [editDialogEntry, setEditDialogEntry] = useState(null);
  const [editDialogStep, setEditDialogStep] = useState('form');
  const [editDialogResultEntry, setEditDialogResultEntry] = useState(null);
  const [editDialogSchema, setEditDialogSchema] = useState(null);
  const [editDialogText, setEditDialogText] = useState('');
  const [editDialogPayload, setEditDialogPayload] = useState(emptyObservationPayload);
  const [editDialogSaving, setEditDialogSaving] = useState(false);
  const reviewInputRef = useRef(null);

  const selectedStudent = useMemo(
    () => studentOptions.find((student) => String(student.id) === String(selectedStudentId)) || null,
    [studentOptions, selectedStudentId],
  );

  useEffect(() => {
    setStudentSchema(null);
    setFormPayload(emptyObservationPayload());
    setCreateDialogOpen(false);
    setCreateDialogStep('form');
    setCreateDialogEntry(null);
    setEditDialogEntry(null);
    setEditDialogStep('form');
    setEditDialogResultEntry(null);
    setEditDialogSchema(null);
    setEditDialogText('');
    setEditDialogPayload(emptyObservationPayload());
    setEditDialogSaving(false);
  }, [selectedStudentId]);

  useEffect(() => {
    if (!openTarget?.requestId) return;
    setPendingOpenTarget(openTarget);
  }, [openTarget]);

  useEffect(() => {
    const loadClasses = async () => {
      if (!schoolId) return;
      setLoadingClasses(true);
      try {
        const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`));
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
      const classes = (data || [])
        .map((item) => {
          if (typeof item === 'string') {
            return { id: null, name: item };
          }
          return { id: item.id, name: item.name };
        })
        .filter((cls) => cls.name)
        .sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr-TR'));
      setClassOptions(classes);
      if (!selectedClass && classes.length === 1) {
        setSelectedClass(classes[0]);
        }
      } catch (_) {
        onError?.('Şubeler yüklenemedi');
      } finally {
        setLoadingClasses(false);
      }
    };

    loadClasses();
  }, [schoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadStudents = async () => {
      if (!schoolId || !selectedClass) {
        setStudentOptions([]);
        setSelectedStudentId('');
        setLoadedStudentClassId('');
        return;
      }
      setLoadingStudents(true);
      setLoadedStudentClassId('');
      try {
        const classId = selectedClass?.id;
        if (!classId) {
          setStudentOptions([]);
          setSelectedStudentId('');
          setLoadedStudentClassId('');
          setLoadingStudents(false);
          return;
        }
        const res = await fetchJson(
          apiUrl(`/admin/students/${encodeURIComponent(schoolId)}/by-class/${encodeURIComponent(classId)}`),
        );
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const mapped = (data || []).map((student) => ({
          id: String(student.id),
          name: student.name,
          surname: student.surname,
          class_name: student.class_name,
          label: `${student.name || ''} ${student.surname || ''}`.trim() || student.id,
        }));
        setStudentOptions(mapped);
        setLoadedStudentClassId(String(classId));
        if (mapped.length === 1) {
          setSelectedStudentId(String(mapped[0].id));
        } else if (!mapped.find((student) => String(student.id) === String(selectedStudentId))) {
          setSelectedStudentId('');
        }
      } catch (_) {
        onError?.('Öğrenciler yüklenemedi');
        setStudentOptions([]);
        setSelectedStudentId('');
        setLoadedStudentClassId('');
      } finally {
        setLoadingStudents(false);
      }
    };

    loadStudents();
  }, [schoolId, selectedClass]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pendingOpenTarget || !classOptions.length) return;

    const targetClass = classOptions.find((item) => String(item?.id || '') === String(pendingOpenTarget.classId || '')) || null;
    if (!targetClass) {
      setPendingOpenTarget(null);
      return;
    }

    if (String(selectedClass?.id || '') !== String(targetClass.id || '')) {
      setSelectedClass(targetClass);
      setSelectedStudentId('');
      setEntries([]);
    }
  }, [pendingOpenTarget, classOptions, selectedClass]);

  useEffect(() => {
    if (!pendingOpenTarget) return;
    if (String(selectedClass?.id || '') !== String(pendingOpenTarget.classId || '')) return;
    if (loadingStudents) return;
    if (String(loadedStudentClassId || '') !== String(pendingOpenTarget.classId || '')) return;

    const targetStudent = studentOptions.find((item) => String(item?.id || '') === String(pendingOpenTarget.studentId || '')) || null;
    if (!targetStudent) {
      setPendingOpenTarget(null);
      return;
    }

    if (String(selectedStudentId || '') !== String(targetStudent.id || '')) {
      setSelectedStudentId(targetStudent.id);
      setPendingOpenTarget(null);
      setTimeout(() => reviewInputRef.current?.focus?.(), 0);
      return;
    }

    setPendingOpenTarget(null);
    setTimeout(() => reviewInputRef.current?.focus?.(), 0);
  }, [pendingOpenTarget, selectedClass, studentOptions, loadingStudents, loadedStudentClassId, selectedStudentId]);

  useEffect(() => {
    const loadSchema = async () => {
      if (!selectedStudent) {
        setStudentSchema(null);
        setFormPayload(emptyObservationPayload());
        return;
      }

      setLoadingSchema(true);
      try {
        const schema = await getObservationSchema(schoolId, selectedStudent.id);
        setStudentSchema(schema || null);
        setFormPayload(buildPayloadForSchema(schema, null));
      } catch (_) {
        setStudentSchema(null);
        setFormPayload(emptyObservationPayload());
        onError?.('Gözlem formu yüklenemedi');
      } finally {
        setLoadingSchema(false);
      }
    };

    loadSchema();
  }, [schoolId, selectedStudent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadEntries = async () => {
      if (!selectedStudent) {
        setEntries([]);
        return;
      }
      setLoadingEntries(true);
      try {
        const data = await listObservationEntries(schoolId, selectedStudent.id);
        setEntries(Array.isArray(data) ? data : []);
      } catch (_) {
        onError?.('Gözlem kaydı yüklenemedi');
      } finally {
        setLoadingEntries(false);
      }
    };

    loadEntries();
  }, [schoolId, selectedStudent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditDialog = (entry) => {
    setEditDialogEntry(entry);
    setEditDialogStep('form');
    setEditDialogResultEntry(null);
    if (isStructuredObservationEntry(entry)) {
      const schema = entrySchema(entry, studentSchema);
      setEditDialogSchema(schema);
      setEditDialogPayload(buildPayloadForSchema(schema, entry.structured_payload));
      setEditDialogText('');
      return;
    }
    setEditDialogSchema(null);
    setEditDialogText(entry.review_text || '');
    setEditDialogPayload(emptyObservationPayload());
  };

  const closeEditDialog = () => {
    if (editDialogSaving || entryAction.type === 'edit-send') return;
    setEditDialogEntry(null);
    setEditDialogStep('form');
    setEditDialogResultEntry(null);
    setEditDialogSchema(null);
    setEditDialogText('');
    setEditDialogPayload(emptyObservationPayload());
  };

  const handleEditLeaveDraft = () => {
    setEditDialogEntry(null);
    setEditDialogStep('form');
    setEditDialogResultEntry(null);
    setEditDialogSchema(null);
    setEditDialogText('');
    setEditDialogPayload(emptyObservationPayload());
  };

  const handleEditSave = async () => {
    if (!editDialogEntry) return;
    setEditDialogSaving(true);
    try {
      const payload = isStructuredObservationEntry(editDialogEntry)
        ? { structured_payload: editDialogPayload }
        : editDialogText.trim();
      const updated = await updateObservationEntry(
        schoolId,
        editDialogEntry.entry_id,
        payload,
      );
      setEntries((prev) => prev.map((entry) => (entry.entry_id === updated.entry_id ? updated : entry)));
      onSuccess?.('Gözlem güncellendi');
      setEditDialogResultEntry(updated);
      setEditDialogStep('summary');
    } catch (err) {
      onError?.(err?.message || 'Gözlem güncellenemedi');
    } finally {
      setEditDialogSaving(false);
    }
  };

  const handleEditSend = async () => {
    if (!editDialogResultEntry?.entry_id) return;
    setEntryAction({ id: editDialogResultEntry.entry_id, type: 'edit-send' });
    try {
      const updated = await sendObservationEntry(schoolId, editDialogResultEntry.entry_id);
      setEntries((prev) => prev.map((item) => (item.entry_id === updated.entry_id ? updated : item)));
      setEditDialogEntry(null);
      setEditDialogStep('form');
      setEditDialogResultEntry(null);
      setEditDialogSchema(null);
      setEditDialogText('');
      setEditDialogPayload(emptyObservationPayload());
      onSuccess?.('WhatsApp bildirimi kuyruğa eklendi');
    } catch (err) {
      onError?.(err?.message || 'WhatsApp gönderimi başarısız');
    } finally {
      setEntryAction({ id: '', type: '' });
    }
  };

  const handleSave = async () => {
    if (!selectedStudent) return;
    setSaving(true);
    try {
      const payload = studentSchema
        ? { structured_payload: formPayload }
        : (formPayload.note || '').trim();
      const entry = await createObservationEntry(schoolId, selectedStudent.id, payload);
      setEntries((prev) => [entry, ...prev]);
      setCreateDialogEntry(entry);
      setCreateDialogStep('summary');
      onSuccess?.('Gözlem kaydedildi');
    } catch (err) {
      onError?.(err?.message || 'Gözlem kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const openCreateDialog = () => {
    if (!selectedStudent) return;
    setCreateDialogStep('form');
    setCreateDialogEntry(null);
    setFormPayload(buildPayloadForSchema(studentSchema, null));
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    if (saving || entryAction.type === 'create-send') return;
    setCreateDialogOpen(false);
    setCreateDialogStep('form');
    setCreateDialogEntry(null);
    setFormPayload(buildPayloadForSchema(studentSchema, null));
  };

  const handleCreateLeaveDraft = () => {
    setCreateDialogOpen(false);
    setCreateDialogStep('form');
    setCreateDialogEntry(null);
    setFormPayload(buildPayloadForSchema(studentSchema, null));
  };

  const handleCreateSend = async () => {
    if (!createDialogEntry?.entry_id) return;
    setEntryAction({ id: createDialogEntry.entry_id, type: 'create-send' });
    try {
      const updated = await sendObservationEntry(schoolId, createDialogEntry.entry_id);
      setEntries((prev) => prev.map((item) => (item.entry_id === updated.entry_id ? updated : item)));
      setCreateDialogOpen(false);
      setCreateDialogStep('form');
      setCreateDialogEntry(null);
      setFormPayload(buildPayloadForSchema(studentSchema, null));
      onSuccess?.('WhatsApp bildirimi kuyruğa eklendi');
    } catch (err) {
      onError?.(err?.message || 'WhatsApp gönderimi başarısız');
    } finally {
      setEntryAction({ id: '', type: '' });
    }
  };

  const openDeleteDialog = (entry) => {
    setDeleteDialogEntry(entry);
  };

  const closeDeleteDialog = () => {
    const isBusy = deleteDialogEntry && entryAction.type === 'delete' && entryAction.id === deleteDialogEntry.entry_id;
    if (isBusy) return;
    setDeleteDialogEntry(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialogEntry) return;
    const entry = deleteDialogEntry;
    setEntryAction({ id: entry.entry_id, type: 'delete' });
    try {
      await deleteObservationEntry(schoolId, entry.entry_id);
      setEntries((prev) => prev.filter((item) => item.entry_id !== entry.entry_id));
      onSuccess?.('Gözlem silindi');
      setDeleteDialogEntry(null);
    } catch (err) {
      onError?.(err?.message || 'Gözlem silinemedi');
    } finally {
      setEntryAction({ id: '', type: '' });
    }
  };

  const handleSend = async (entry) => {
    setEntryAction({ id: entry.entry_id, type: 'send' });
    try {
      const updated = await sendObservationEntry(schoolId, entry.entry_id);
      setEntries((prev) => prev.map((item) => (item.entry_id === updated.entry_id ? updated : item)));
      onSuccess?.('WhatsApp bildirimi kuyruğa eklendi');
    } catch (err) {
      onError?.(err?.message || 'WhatsApp gönderimi başarısız');
    } finally {
      setEntryAction({ id: '', type: '' });
    }
  };

  const showTeacherHint = user?.role === 'teacher';
  const totalEntries = entries.length;
  const sentCount = entries.filter((entry) => normalizeMessageStatus(entry.message_status) === 'sent').length;
  const pendingCount = entries.filter((entry) => normalizeMessageStatus(entry.message_status) === 'pending').length;
  const failedCount = entries.filter((entry) => normalizeMessageStatus(entry.message_status) === 'failed').length;
  const unknownCount = entries.filter((entry) => normalizeMessageStatus(entry.message_status) === 'unknown').length;
  const draftCount = Math.max(totalEntries - sentCount - pendingCount - failedCount - unknownCount, 0);
  const deleteDialogBusy = Boolean(
    deleteDialogEntry && entryAction.type === 'delete' && entryAction.id === deleteDialogEntry.entry_id,
  );
  const editDialogIsStructured = isStructuredObservationEntry(editDialogEntry);
  const editDialogSaveDisabled = editDialogSaving
    || (editDialogIsStructured
      ? !editDialogSchema || !hasTeacherObservation(editDialogPayload)
      : !editDialogText.trim());
  const createDialogSaveDisabled = saving
    || loadingSchema
    || (studentSchema ? !hasTeacherObservation(formPayload) : !(formPayload.note || '').trim());
  const createDialogSending = entryAction.type === 'create-send' && Boolean(createDialogEntry?.entry_id);
  const editDialogSending = entryAction.type === 'edit-send' && Boolean(editDialogResultEntry?.entry_id);

  const renderEntrySummary = (entry, options = {}) => {
    if (!entry) return null;
    const {
      overline = 'Taslak kaydedildi',
      title = 'Gözlem özeti hazır',
      description = 'Gözlem kaydı oluşturuldu. İsterseniz şimdi veliye gönderin, isterseniz taslak olarak bırakın.',
    } = options;
    const statusKey = normalizeMessageStatus(entry.message_status);
    const status = statusConfig[statusKey] || statusConfig.draft;

    return (
      <Stack spacing={2.25}>
        <Box>
          <Typography variant="overline" color="success.main">
            {overline}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {description}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selectedStudent?.class_name ? (
            <Chip label={selectedStudent.class_name} size="small" variant="outlined" color="primary" />
          ) : null}
          <Chip label={status.label} size="small" color={status.chipColor} variant={status.chipVariant} />
        </Box>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#FCFCFC' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {getEntryAuthorLabel(entry)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
            {formatDate(entry.created_at)}
          </Typography>
          {renderStructuredDetails(entry) || (
            <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
              {entry.review_text}
            </Typography>
          )}
        </Paper>
      </Stack>
    );
  };

  const renderCreateSummary = () => {
    return renderEntrySummary(createDialogEntry);
  };

  const renderEditSummary = () => renderEntrySummary(editDialogResultEntry, {
    overline: 'Değişiklikler kaydedildi',
    description: 'Güncellenen gözlem kaydı hazır. İsterseniz şimdi veliye gönderin, isterseniz taslak olarak bırakın.',
  });

  const renderStructuredDetails = (entry) => {
    if (!isStructuredObservationEntry(entry)) return null;
    const schema = entrySchema(entry, studentSchema);
    if (!schema) return null;
    const payload = normalizeObservationPayload(entry.structured_payload);
    const sections = [
      { title: 'Ders durumları', fields: schema.academic_fields || [], groupKey: 'academic' },
      { title: 'Genel durumlar', fields: schema.general_fields || [], groupKey: 'general' },
    ].map((section) => ({
      ...section,
      fields: section.fields.filter((field) => Boolean(payload?.[section.groupKey]?.[field.key])),
    })).filter((section) => section.fields.length > 0);

    return (
      <Stack spacing={1.35} sx={{ mt: 0.75, pt: 1.25, borderTop: '1px solid rgba(148, 163, 184, 0.18)' }}>
        {payload.note ? (
          <Stack spacing={0.45}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Öğretmen gözlemi
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {payload.note}
            </Typography>
          </Stack>
        ) : null}
        {sections.map((section) => (
          <Stack key={section.title} spacing={0.8}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {section.title}
            </Typography>
            {section.fields.map((field) => (
              <Box key={`${section.groupKey}-${field.key}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ pr: 1 }}>
                  {field.label}
                </Typography>
                <Chip
                  size="small"
                  label={scaleLabels[String(payload?.[section.groupKey]?.[field.key] || '')] || '—'}
                  color={observationScaleTone(String(payload?.[section.groupKey]?.[field.key] || ''))}
                  variant={String(payload?.[section.groupKey]?.[field.key] || '') ? 'filled' : 'outlined'}
                  sx={(theme) => ({ ...observationScaleChipSx(String(payload?.[section.groupKey]?.[field.key] || ''))(theme), flexShrink: 0 })}
                />
              </Box>
            ))}
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ bgcolor: '#F9FAFB', minHeight: '100%', py: { xs: 2, md: 4 }, px: { xs: 2, md: 4 } }}>
      <Stack spacing={3}>
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <RateReviewIcon color="primary" sx={{ fontSize: 36 }} />
              <Box>
                <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.5 }}>
                  Gözlem Günlüğü
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Öğrenci gözlemlerini düzenleyin ve velilerle paylaşın
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem sx={{ borderColor: '#E0E0E0' }} />}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Toplam kayıt
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {totalEntries || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Gönderilenler
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {sentCount || '—'}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }} color="text.secondary">
                Öğrenci seçimi
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Şube ve öğrenci seçerek gözlem geçmişine ulaşın.
              </Typography>
              <Stack spacing={2.5}>
                <Autocomplete
                  fullWidth
                  options={classOptions}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  value={selectedClass || null}
                  onChange={(_, value) => {
                    setSelectedClass(value || null);
                    setSelectedStudentId('');
                    setEntries([]);
                  }}
                  loading={loadingClasses}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Şube"
                      placeholder="Şube seçin"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingClasses ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Autocomplete
                  fullWidth
                  options={studentOptions}
                  getOptionLabel={(option) => option.label || ''}
                  value={selectedStudent || null}
                  onChange={(_, value) => {
                    setSelectedStudentId(String(value?.id || ''));
                  }}
                  loading={loadingStudents}
                  disabled={!selectedClass}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Öğrenci"
                      placeholder={selectedClass?.name ? 'Öğrenci seçin' : 'Önce şube seçin'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingStudents ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {showTeacherHint && (
                  <Typography variant="caption" color="text.secondary">
                    Öğretmenler yalnızca kendi girdikleri kayıtları görebilir.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              {selectedStudent ? (
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Seçilen öğrenci
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedStudent.label}
                      </Typography>
                    </Box>
                    {selectedStudent.class_name && (
                      <Chip label={selectedStudent.class_name} size="small" color="primary" variant="outlined" />
                    )}
                    <Chip label={`Taslak: ${draftCount}`} size="small" color="warning" variant="outlined" />
                    {pendingCount > 0 && (
                      <Chip label={`Kuyrukta: ${pendingCount}`} size="small" color="warning" variant="outlined" />
                    )}
                    {failedCount > 0 && (
                      <Chip label={`Hata: ${failedCount}`} size="small" color="error" variant="outlined" />
                    )}
                    <Chip label={`Gönderildi: ${sentCount}`} size="small" color="success" variant="outlined" />
                    {unknownCount > 0 && <Chip label={`Durum belirsiz: ${unknownCount}`} size="small" color="warning" variant="outlined" />}
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                      Yeni gözlem ekleyin
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Yeni gözlem kaydı eklemek için formu ayrı pencerede açın. Bu sayfa geçmiş kayıtları takip etmek için sade kalır.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Button variant="contained" onClick={openCreateDialog} disabled={!selectedStudent || loadingSchema}>
                        Yeni gözlem ekle
                      </Button>
                    </Box>
                  </Box>

                  <Divider />
                  {loadingEntries ? (
                    <Stack spacing={2}>
                      {[0, 1, 2].map((idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Skeleton variant="text" width="40%" />
                          <Skeleton variant="text" width="30%" />
                          <Skeleton variant="rectangular" height={60} sx={{ mt: 2, borderRadius: 1 }} />
                        </Paper>
                      ))}
                    </Stack>
                  ) : entries.length ? (
                    <Stack spacing={2}>
                      {entries.map((entry) => {
                        const isWorking = entryAction.id === entry.entry_id;
                        const isSending = isWorking && entryAction.type === 'send';
                        const isDeleting = isWorking && entryAction.type === 'delete';
                        const statusKey = normalizeMessageStatus(entry.message_status);
                        const status = statusConfig[statusKey] || statusConfig.draft;
                        const tone = statusTone[statusKey] || statusTone.draft;
                        const authorLabel = getEntryAuthorLabel(entry);
                        const isStructuredEntry = isStructuredObservationEntry(entry);
                        return (
                          <Paper
                            key={entry.entry_id}
                            variant="outlined"
                            sx={{
                              borderRadius: 3,
                              p: 2.25,
                              borderColor: tone.border,
                              background: tone.background,
                              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)',
                            }}
                          >
                            <Stack spacing={1.75}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {authorLabel}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                                    {formatDate(entry.created_at)}
                                  </Typography>
                                  {statusKey === 'sent' && entry.parent_notified_at && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.65, fontWeight: 600 }}>
                                      Gönderim kabul edildi: {formatDate(entry.parent_notified_at)}
                                    </Typography>
                                  )}
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                {entry.recorded_by_role && (
                                  <Chip
                                    size="small"
                                    label={roleLabels[entry.recorded_by_role] || entry.recorded_by_role}
                                    color={entry.recorded_by_role === 'teacher' ? 'primary' : 'default'}
                                    variant="outlined"
                                  />
                                )}
                                {(entry.student_class || selectedStudent.class_name) && (
                                  <Chip size="small" variant="outlined" label={entry.student_class || selectedStudent.class_name} />
                                )}
                                <Chip
                                  size="small"
                                  color={status.chipColor}
                                  variant={status.chipVariant}
                                  label={status.label}
                                />
                              </Stack>
                              </Box>

                              {isStructuredEntry ? (
                                <Typography variant="caption" color="text.secondary">
                                  Bu kayıt yapılandırılmış alanlarla tutulur
                                </Typography>
                              ) : entry.review_text ? (
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    bgcolor: 'rgba(255,255,255,0.82)',
                                    borderColor: 'rgba(148, 163, 184, 0.24)',
                                  }}
                                >
                                  <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                                    {entry.review_text}
                                  </Typography>
                                </Paper>
                              ) : null}

                              {isStructuredEntry ? renderStructuredDetails(entry) : null}

                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                              <Tooltip title={entry.parent_notified_at ? 'Bildirim oluşturulan kayıtlar düzenlenemez' : 'Düzenle'}>
                                <span>
                                  <Button
                                    aria-label="Gözlem kaydını düzenle"
                                    disabled={Boolean(entry.parent_notified_at) || isWorking}
                                    onClick={() => openEditDialog(entry)}
                                    startIcon={<EditOutlinedIcon fontSize="small" />}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 999,
                                      textTransform: 'none',
                                      color: 'text.secondary',
                                      '&:hover': {
                                        bgcolor: '#F5F5F5',
                                        color: 'text.primary',
                                      },
                                    }}
                                  >
                                    Düzenle
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title={entry.parent_notified_at ? 'Bildirim oluşturulan kayıtlar silinemez' : 'Sil'}>
                                <span>
                                  <Button
                                    aria-label="Gözlem kaydını sil"
                                    disabled={Boolean(entry.parent_notified_at) || isWorking}
                                    onClick={() => openDeleteDialog(entry)}
                                    startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineOutlinedIcon fontSize="small" />}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 999,
                                      textTransform: 'none',
                                      color: 'error.main',
                                      '&:hover': {
                                        bgcolor: '#FDECEC',
                                        color: 'error.dark',
                                      },
                                    }}
                                  >
                                    Sil
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title={entry.parent_notified_at ? (statusConfig[statusKey]?.label || 'Bildirim oluşturuldu') : 'WhatsApp mesajlarını gönder'}>
                                <span>
                                  <Button
                                    aria-label={
                                      entry.parent_notified_at ? (statusConfig[statusKey]?.label || 'Bildirim oluşturuldu') : 'WhatsApp mesajlarını gönder'
                                    }
                                    disabled={Boolean(entry.parent_notified_at) || isWorking}
                                    onClick={() => handleSend(entry)}
                                    startIcon={statusKey === 'sent' ? <CheckCircleOutlineIcon fontSize="small" /> : entry.parent_notified_at ? null : isSending ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon fontSize="small" />}
                                    size="small"
                                    variant={entry.parent_notified_at ? 'outlined' : 'contained'}
                                    color={entry.parent_notified_at ? (status.chipColor === 'default' ? 'inherit' : status.chipColor) : 'success'}
                                    sx={{
                                      borderRadius: 999,
                                      textTransform: 'none',
                                      '&:hover': {
                                        bgcolor: entry.parent_notified_at ? '#F1F8E9' : undefined,
                                      },
                                    }}
                                  >
                                    {entry.parent_notified_at ? (statusConfig[statusKey]?.label || 'Bildirim oluşturuldu') : 'WhatsApp ile gönder'}
                                  </Button>
                                </span>
                              </Tooltip>
                              </Box>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: '#F9FAFB' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Henüz gözlem yok
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto' }}>
                        Öğrencinin gözlem günlüğüne ilk taslağı ekleyin ve hazır olduğunuzda velilere WhatsApp üzerinden gönderin.
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              ) : (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Gözlem kaydetmek için önce bir öğrenci seçin.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Soldaki karttan şube ve öğrenci seçtiğinizde geçmiş raporlamalar burada listelenecek.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Stack>
      <Dialog
        open={Boolean(deleteDialogEntry)}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Gözlem silinsin mi?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Bu gözlem kalıcı olarak silinecek. İşlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteDialogBusy}>İptal</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogBusy}
          >
            {deleteDialogBusy ? 'Siliniyor…' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editDialogEntry)} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editDialogStep === 'summary' ? 'Gözlem özeti' : 'Gözlem kaydını düzenleyin'}</DialogTitle>
        <DialogContent dividers>
          {editDialogStep === 'summary' ? renderEditSummary() : editDialogIsStructured ? (
            <ObservationStructuredForm
              schema={editDialogSchema}
              payload={editDialogPayload}
              onChange={setEditDialogPayload}
            />
          ) : (
            <TextField
              label="Gözlem"
              multiline
              minRows={5}
              fullWidth
              value={editDialogText}
              onChange={(e) => setEditDialogText(e.target.value)}
              autoFocus
            />
          )}
        </DialogContent>
        <DialogActions>
          {editDialogStep === 'summary' ? (
            <>
              <Button onClick={handleEditLeaveDraft} disabled={editDialogSending}>
                Taslak olarak bırak
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleEditSend}
                disabled={editDialogSending}
                startIcon={editDialogSending ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon fontSize="small" />}
              >
                {editDialogSending ? 'Gönderiliyor…' : 'WhatsApp ile gönder'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={closeEditDialog} disabled={editDialogSaving}>
                İptal
              </Button>
              <Button
                variant="contained"
                onClick={handleEditSave}
                disabled={editDialogSaveDisabled}
              >
                {editDialogSaving ? 'Kaydediliyor…' : 'Değişiklikleri kaydet'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={closeCreateDialog} fullWidth maxWidth="md">
        <DialogTitle>{createDialogStep === 'summary' ? 'Gözlem özeti' : 'Yeni gözlem ekleyin'}</DialogTitle>
        <DialogContent dividers>
          {createDialogStep === 'summary' ? renderCreateSummary() : selectedStudent ? (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Öğrenci
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedStudent.label}
                </Typography>
                {selectedStudent.class_name ? (
                  <Chip label={selectedStudent.class_name} size="small" color="primary" variant="outlined" sx={{ mt: 0.75 }} />
                ) : null}
              </Box>

              {loadingSchema ? (
                <Stack spacing={1.5}>
                  <Skeleton variant="text" width="30%" />
                  <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                </Stack>
              ) : studentSchema ? (
                <ObservationStructuredForm
                  schema={studentSchema}
                  payload={formPayload}
                  onChange={setFormPayload}
                  noteInputRef={reviewInputRef}
                />
              ) : (
                <TextField
                  label="Yeni gözlem"
                  multiline
                  minRows={5}
                  fullWidth
                  inputRef={reviewInputRef}
                  value={formPayload.note}
                  onChange={(e) => setFormPayload((prev) => ({ ...prev, note: e.target.value }))}
                  placeholder="Gözlemlerinizi ve velilere iletmek istediğiniz notları yazın..."
                />
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          {createDialogStep === 'summary' ? (
            <>
              <Button onClick={handleCreateLeaveDraft} disabled={createDialogSending}>
                Taslak olarak bırak
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleCreateSend}
                disabled={createDialogSending}
                startIcon={createDialogSending ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon fontSize="small" />}
              >
                {createDialogSending ? 'Gönderiliyor…' : 'WhatsApp ile gönder'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={closeCreateDialog} disabled={saving}>
                İptal
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={createDialogSaveDisabled}>
                {saving ? 'Kaydediliyor…' : 'Taslağı Kaydet'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
